import { useCallback, useEffect, useState } from "react";

export function isStandalone() {
  try {
    return (
      window.matchMedia?.("(display-mode: standalone)")?.matches === true ||
      window.matchMedia?.("(display-mode: minimal-ui)")?.matches === true ||
      window.navigator.standalone === true
    );
  } catch {
    return false;
  }
}

export function isIOS() {
  const ua = window.navigator.userAgent || "";
  const iOS = /iphone|ipad|ipod/i.test(ua);
  // iPadOS 13+ reports as Mac; detect touch Macs as iPad-like.
  const iPadOS = navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1;
  return (iOS || iPadOS) && !window.MSStream;
}

// Hook exposing live PWA-install capability. Relies on the beforeinstallprompt
// event captured early in index.html (window.deferredInstallPrompt).
export function usePwaInstall() {
  const [canInstall, setCanInstall] = useState(!!window.deferredInstallPrompt);
  const [standalone, setStandalone] = useState(isStandalone());

  useEffect(() => {
    const onAvail = () => setCanInstall(!!window.deferredInstallPrompt);
    const onInstalled = () => {
      setCanInstall(false);
      setStandalone(true);
    };
    window.addEventListener("pwa-installable", onAvail);
    window.addEventListener("pwa-installed", onInstalled);
    return () => {
      window.removeEventListener("pwa-installable", onAvail);
      window.removeEventListener("pwa-installed", onInstalled);
    };
  }, []);

  const promptInstall = useCallback(async () => {
    const evt = window.deferredInstallPrompt;
    if (!evt) return null;
    evt.prompt();
    const choice = await evt.userChoice;
    window.deferredInstallPrompt = null;
    setCanInstall(false);
    return choice?.outcome; // "accepted" | "dismissed"
  }, []);

  return { canInstall, standalone, ios: isIOS(), promptInstall };
}

export function registerSW() {
  if ("serviceWorker" in navigator) {
    window.addEventListener("load", () => {
      navigator.serviceWorker.register("/service-worker.js").catch(() => {});
    });
  }
}
